import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { USER, XLSX } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError, interpretDataIntoXlsxFormat } from "../helpers";
import { ICustomer } from "./customersReducer";
import { IDriver } from "./driversReducer";
import { tryConfirmLogin } from "./authReducer";
import xlsx from "json-as-xlsx";
import { IStaff } from "./staffsReducer";
import { IServiceResource } from "./servicesResourcesReducer";

class ExportProductsCategoriesErrs {
    form: string | null = null;
}

export interface IAvailableProductCategory {
    id: number;
    _id: string;
    name: string;
    imgSrc: string;
    pricePerHour: number;
    count: number;
}

export interface IProductCategory {
    _id: string;
    name: string;
    imgSrc?: string;
    children?: IProductCategory[],
    parentId?: string;

    createdAt: string;

    deletedAt?: number;
}

export interface IProductCategoryForDelivery {
    _id: string;
    name: string;
    imgSrc: string;
    pricePerHour: number;
    pricePerDay: number;
    plannedDays: number;
    count: number;
    customer: ICustomer;
    driver: IDriver;

}

export interface IProductCategoryForPickUp {
    _id: string;
    name: string;
    imgSrc: string;
    pricePerHour: number;
    pricePerDay: number;
    daysRemaining: number;
    count: number;
    customer: ICustomer;
    driver: IDriver | null;

}

export interface IPackage2Deliver {
    items: IProductCategoryForDelivery[];
}

export interface IPackage2PickUp {
    item: IProductCategoryForPickUp[];
}

class RegProductCategoryErrs {
    index?: any;

    form: string | null = null;
    description: string | null = null;
    virtualLink: string | null = null;
    staffId?: string;
    name: string | null = null;
    productCategoryImg: string | null = null;
    pricePerDay: string | null = null;
    count: string | null = null;
    activationDate: string | null = null;
    deactivationDate: string | null = null;
}

export interface IProductsCategoriesState {
    registeringProductCategory: boolean;
    regProductCategoryErrs: RegProductCategoryErrs;
    fetchingProductsCategories: boolean;
    fetchProductsCategoriesErr: string | null;
    productsCategories: IProductCategory[];
    togglingLike: boolean;
    toggleLikeErr: string | null;
    favouriteProductsCategories: IProductCategory[];
    mainProductCategory: IProductCategory | null;
    availableItems: IAvailableProductCategory[];
    updatingProductCategory: boolean;
    updateProductCategoryErrs: RegProductCategoryErrs;
    exportingProductsCategories: boolean | null;
    exportedProductsCategories: boolean | null;
    exportProductsCategoriesErrs: ExportProductsCategoriesErrs;
    importingProductsCategories: boolean;
    triedImportingProductsCategories: boolean | null;
    importedProductsCategories: boolean | null;
    importProductsCategoriesErrs: RegProductCategoryErrs[];
    deletingProductCategory: boolean;
    deletedProductCategory: boolean;
    deleteProductCategoryErr: string | null;
}

const initialState = {
    registeringProductCategory: false,
    regProductCategoryErrs: new RegProductCategoryErrs(),
    fetchingProductsCategories: false,
    fetchProductsCategoriesErr: null,
    productsCategories: [],
    mainProductCategory: null,
    availableItems: [],
    updatingProductCategory: false,
    updateProductCategoryErrs: new RegProductCategoryErrs(),
    togglingLike: false,
    toggleLikeErr: null,
    favouriteProductsCategories: [],
    exportingProductsCategories: false,
    exportedProductsCategories: false,
    exportProductsCategoriesErrs: new ExportProductsCategoriesErrs(),
    triedImportingProductsCategories: false,
    importingProductsCategories: false,
    importedProductsCategories: false,
    importProductsCategoriesErrs: [],
    deletingProductCategory: false,
    deletedProductCategory: false,
    deleteProductCategoryErr: null
} as IProductsCategoriesState

const productsCategoriesSlice = createSlice({
    name: 'productsCategories',
    initialState,
    reducers: {
        clearRegProductCategoryFieldErr: (state: IProductsCategoriesState, action: PayloadAction<string>) => {
            state.regProductCategoryErrs[action.payload as keyof RegProductCategoryErrs] = null;
        },
        clearRegProductCategoryErrs: (state: IProductsCategoriesState) => {
            state.regProductCategoryErrs = new RegProductCategoryErrs();
        },
        tryResetRegProductCategoryFormErr: (state: IProductsCategoriesState) => {
            let shouldReset = true;

            for (let key in state.regProductCategoryErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.regProductCategoryErrs[key as keyof RegProductCategoryErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.regProductCategoryErrs.form = null;
            }
        },
        regProductCategoryStart: (state: IProductsCategoriesState) => {
            state.registeringProductCategory = true;
        },
        regProductCategoryPass: (state: IProductsCategoriesState) => {
            state.registeringProductCategory = false;
        },
        regProductCategoryFail: (state: IProductsCategoriesState, action: PayloadAction<IFieldError[]>) => {
            state.registeringProductCategory = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.regProductCategoryErrs) {
                    if (err.field === key) {
                        state.regProductCategoryErrs[key as keyof RegProductCategoryErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.regProductCategoryErrs.form) {
                state.regProductCategoryErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchProductsCategories: (state: IProductsCategoriesState) => {
            state.regProductCategoryErrs = new RegProductCategoryErrs();
            state.fetchProductsCategoriesErr = null;
        },

        clearFetchProductCategoryErrs: (state: IProductsCategoriesState) => {
            state.regProductCategoryErrs = new RegProductCategoryErrs();
            state.fetchProductsCategoriesErr = null;

        },

        fetchProductsCategoriesStart: (state: IProductsCategoriesState) => {
            state.fetchingProductsCategories = true;
        },
        fetchProductsCategoriesPass: (state: IProductsCategoriesState, action: PayloadAction<IProductCategory[]>) => {
            state.fetchingProductsCategories = false;

            state.productsCategories = action.payload;
        },
        fetchProductsCategoriesFail: (state: IProductsCategoriesState, action: PayloadAction<string>) => {
            state.fetchingProductsCategories = false;
            state.fetchProductsCategoriesErr = action.payload;
        },
        setMainProductCategory: (state: IProductsCategoriesState, action: PayloadAction<IProductCategory | null>) => {
            state.mainProductCategory = action.payload;
        },
        clearUpdateProductCategoryFieldErr: (state: IProductsCategoriesState, action: PayloadAction<string>) => {
            state.updateProductCategoryErrs[action.payload as keyof RegProductCategoryErrs] = null;
        },
        clearUpdateProductCategoryErrs: (state: IProductsCategoriesState) => {
            state.updateProductCategoryErrs = new RegProductCategoryErrs();
        },
        tryResetUpdateProductCategoryFormErr: (state: IProductsCategoriesState) => {
            let shouldReset = true;

            for (let key in state.updateProductCategoryErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.updateProductCategoryErrs[key as keyof RegProductCategoryErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.updateProductCategoryErrs.form = null;
            }
        },
        updateProductCategoryStart: (state: IProductsCategoriesState) => {
            state.updatingProductCategory = true;
        },
        updateProductCategoryPass: (state: IProductsCategoriesState, action: PayloadAction<IProductCategory>) => {
            state.updatingProductCategory = false;
            // state.mainCustomer = action.payload;
        },
        updateProductCategoryFail: (state: IProductsCategoriesState, action: PayloadAction<IFieldError[]>) => {
            state.updatingProductCategory = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.updateProductCategoryErrs) {
                    if (err.field === key) {
                        state.updateProductCategoryErrs[key as keyof RegProductCategoryErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.updateProductCategoryErrs.form) {
                state.updateProductCategoryErrs.form = 'Form incorrectly filled.';
            }
        },
        clearToggleLikeErr:(state: IProductsCategoriesState) => {
            state.toggleLikeErr = null;
        },
        toggleLikeStart: (state: IProductsCategoriesState) => {
            state.toggleLikeErr = null;
            state.togglingLike = true;
        },
        toggleLikePass: (state: IProductsCategoriesState, action: PayloadAction<IProductCategory[]>) => {
            state.favouriteProductsCategories = action.payload;
            state.togglingLike = false;
        },
        toggleLikeFail: (state: IProductsCategoriesState, action: PayloadAction<string>) => {
            state.toggleLikeErr = action.payload;
            state.togglingLike = false;
        },
        setFavouriteProductsCategories: (state: IProductsCategoriesState, action: PayloadAction<IProductCategory[]>) => {
            state.favouriteProductsCategories = action.payload;
        },
        clearExportProductsCategoriesFieldErr: (state: IProductsCategoriesState, action: PayloadAction<string>) => {
            state.exportProductsCategoriesErrs[action.payload as keyof ExportProductsCategoriesErrs] = null;
        },
        clearExportProductsCategoriesErrs: (state: IProductsCategoriesState) => {
            state.exportProductsCategoriesErrs = new ExportProductsCategoriesErrs();
        },
        tryResetExportProductsCategoriesFormErr: (state: IProductsCategoriesState) => {
            let shouldReset = true;

            for (let key in state.exportProductsCategoriesErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.exportProductsCategoriesErrs[key as keyof ExportProductsCategoriesErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.exportProductsCategoriesErrs.form = null;
            }
        },
        exportProductsCategoriesStart: (state: IProductsCategoriesState) => {
            state.exportingProductsCategories = true;
            state.exportedProductsCategories = false;
        },
        exportProductsCategoriesPass: (state: IProductsCategoriesState) => {
            state.exportingProductsCategories = false;
            state.exportedProductsCategories = true;
        },
        exportProductsCategoriesFail: (state: IProductsCategoriesState, action: PayloadAction<IFieldError[]>) => {
            state.exportingProductsCategories = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.exportProductsCategoriesErrs) {
                    if (err.field === key) {
                        state.exportProductsCategoriesErrs[key as keyof ExportProductsCategoriesErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.exportProductsCategoriesErrs.form) {
                state.exportProductsCategoriesErrs.form = 'Form incorrectly filled.';
            }
        },
        clearImportProductsCategoriesFieldErr: (state: IProductsCategoriesState, action: PayloadAction<string>) => {
            state.importProductsCategoriesErrs = [];
        },
        clearImportProductsCategoriesErrByIndex: (state: IProductsCategoriesState, action: PayloadAction<number>) => {

            const index = action.payload;

            let newErrs = [];

            if (index < state.importProductsCategoriesErrs.length) {
                for (let i = 0; i < index; i++) {
                    newErrs[i] = state.importProductsCategoriesErrs[i];
                }

                for (let i = index; i < (state.importProductsCategoriesErrs.length - 1); i++) {
                    newErrs[i] = (state.importProductsCategoriesErrs)[i + 1];
                }

                state.importProductsCategoriesErrs = newErrs;
            }
        },
        clearResetTriedImportingProductsCategories: (state: IProductsCategoriesState) => {
            state.triedImportingProductsCategories = false;
        },
        clearImportProductsCategoriesErrs: (state: IProductsCategoriesState) => {
            state.importProductsCategoriesErrs = [];
        },
        tryResetImportProductsCategoriesFormErr: (state: IProductsCategoriesState) => {

        },
        importProductsCategoriesStart: (state: IProductsCategoriesState) => {

            state.importProductsCategoriesErrs = [];

            state.importingProductsCategories = true;
            state.triedImportingProductsCategories = false;
            state.importedProductsCategories = false;

        },
        importProductsCategoriesDone: (state: IProductsCategoriesState) => {
            state.importingProductsCategories = false;
            state.triedImportingProductsCategories = true;
        },
        importProductsCategoriesPass: (state: IProductsCategoriesState) => {
            state.importingProductsCategories = false;
            state.triedImportingProductsCategories = true;
            state.importedProductsCategories = true;
        },
        addErr2ImportProductsCategoriesErrs: (state: IProductsCategoriesState, action: PayloadAction<RegProductCategoryErrs>) => {

            let errs = state.importProductsCategoriesErrs;

            errs = [...errs, action.payload];

            state.importProductsCategoriesErrs = errs;
        },
        importProductsCategoriesFail: (state: IProductsCategoriesState) => {
            state.importingProductsCategories = false;
        },
        clearDeletProductCategoryErr: (state: IProductsCategoriesState) => {
            state.deleteProductCategoryErr = null;
        },
        resetDeletedProductCategory: (state: IProductsCategoriesState) => {
            state.deletedProductCategory = false;
        },
        deleteProductCategoryStart: (state: IProductsCategoriesState) => {
            state.deleteProductCategoryErr = null;
            state.deletedProductCategory = false;
            state.deletingProductCategory = true;
        },
        deleteProductCategoryPass: (state: IProductsCategoriesState) => {
            state.deletedProductCategory = true;
            state.deletingProductCategory = false;
        },
        deleteProductCategoryFail: (state: IProductsCategoriesState, action: PayloadAction<string>) => {
            state.deleteProductCategoryErr = action.payload;
            state.deletingProductCategory = false;
        }
    }
});

export const {
    clearRegProductCategoryFieldErr,
    clearRegProductCategoryErrs,
    tryResetRegProductCategoryFormErr,
    regProductCategoryStart,
    regProductCategoryPass,
    regProductCategoryFail,
    clearFetchProductsCategories,
    fetchProductsCategoriesStart,
    fetchProductsCategoriesPass,
    fetchProductsCategoriesFail,
    setMainProductCategory,
    clearFetchProductCategoryErrs,
    clearUpdateProductCategoryFieldErr,
    clearUpdateProductCategoryErrs,
    tryResetUpdateProductCategoryFormErr,
    updateProductCategoryStart,
    updateProductCategoryPass,
    updateProductCategoryFail,
    setFavouriteProductsCategories,
    clearToggleLikeErr,
    toggleLikeStart,
    toggleLikePass,
    toggleLikeFail,
    clearImportProductsCategoriesErrByIndex,
    clearExportProductsCategoriesErrs,
    clearExportProductsCategoriesFieldErr,
    tryResetExportProductsCategoriesFormErr,
    exportProductsCategoriesStart,
    exportProductsCategoriesPass,
    exportProductsCategoriesFail,
    clearResetTriedImportingProductsCategories,
    clearImportProductsCategoriesErrs,
    clearImportProductsCategoriesFieldErr,
    tryResetImportProductsCategoriesFormErr,
    importProductsCategoriesStart,
    importProductsCategoriesDone,
    importProductsCategoriesPass,
    addErr2ImportProductsCategoriesErrs,
    importProductsCategoriesFail,
    clearDeletProductCategoryErr,
    resetDeletedProductCategory,
    deleteProductCategoryStart,
    deleteProductCategoryPass,
    deleteProductCategoryFail
} = productsCategoriesSlice.actions;


interface ITryRegProductCategoryArg {
    data: FormData;
    authToken: string;
    setRegistered: (truth: boolean) => void;
    setServiceResourceId?: (_id: string | null) => void;
}

export const tryRegProductCategory = createAsyncThunk('productsCategories/tryRegProductCategory', (arg: ITryRegProductCategoryArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/product-category`;

    const { data, authToken, setRegistered, setServiceResourceId } = arg;

    dispatch(regProductCategoryStart());

    axios.post(endpoint, data, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);

        if (res?.data?.serviceResourceId && setServiceResourceId) {
            setServiceResourceId(res?.data?.serviceResourceId);
        }

        dispatch(regProductCategoryPass());
        setRegistered(true);
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(regProductCategoryFail(fieldErrors));
    })
});

interface ITryFetchProductCategoryArg {
    authToken: string;
    type?: string;
}

export const tryFetchProductsCategories = createAsyncThunk('productsCategories/tryFetchProductsCategories', (arg: ITryFetchProductCategoryArg, { dispatch }) => {

    const { authToken, type } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products-categories${type ? `?type=${type}` : ''}`;

    dispatch(fetchProductsCategoriesStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchProductsCategoriesPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(fetchProductsCategoriesFail(data.err));
        } else {
            dispatch(fetchProductsCategoriesFail('Check your network connection.'))
        }

    });
});


interface ITryUpdateProductCategoryArg {
    productCategoryId: string;
    data: FormData;
    authToken: string;
    setUpdated: (truth: boolean) => void
}

export const tryUpdateProductCategory = createAsyncThunk('productsCategories/tryUpdateProductCategory', (arg: ITryUpdateProductCategoryArg, { dispatch }) => {

    const { productCategoryId, data, authToken, setUpdated } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products-categories/${productCategoryId}`;

    dispatch(updateProductCategoryStart());

    axios.put(endpoint, data, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(updateProductCategoryPass(res.data));
        setUpdated(true);
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(updateProductCategoryFail(fieldErrors));
    })
});

interface ITryToggleProductCategoryLikeArg {
    authToken: string;
    productCategoryId: string;
}

export const tryToggleRentalLike = createAsyncThunk('productsCategories/tryToggleLike', (arg: ITryToggleProductCategoryLikeArg, {dispatch}) => {
    const {authToken, productCategoryId} = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products-categories/${productCategoryId}/toggle-like`;

    dispatch(toggleLikeStart())
    axios.post(endpoint, {}, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(toggleLikePass(res.data?.favouriteProductsCategories ?? []));
    })
    .catch(err => {
        const data = err?.response?.data;
        let error = '';

        if (data?.errMsg) {
            error = data.errMsg;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(toggleLikeFail(error));
    });

});

interface ITryExportProductsCategoriesArg {
    format: string;
    authToken: string;
}

export const tryExportProductsCategories = createAsyncThunk("servicesPlans/tryExportProductsCategories", (arg: ITryExportProductsCategoriesArg, { dispatch }) => {

    const { authToken, format } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products-categories-export`;

    dispatch(exportProductsCategoriesStart());
    axios.get(endpoint, {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`
        }
    }).then(res => {
        const data = res.data;

        if (data.length === 0) {
            const fieldErrors = [{ field: 'form', msg: 'Export Empty.' }];

            dispatch(exportProductsCategoriesFail(fieldErrors));
        } else {

            switch (format) {
                case XLSX:
                default:
                    const fileSettings = {
                        fileName: "ProductsCategories"        //TODO: dynamically generate the name, based on the parameters used to fetch the data
                    };

                    try {
                        xlsx(interpretDataIntoXlsxFormat(data), fileSettings);
                    } catch (err) {
                        alert("err");
                        console.log(err);
                    }
                    break;
            }

            dispatch(exportProductsCategoriesPass());
        }
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            console.log(data);
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(exportProductsCategoriesFail(fieldErrors));
    })


});

interface IDeleteProductCategoryArg {
    authToken: string;
    productCategoryId: string;
}

export const tryDeleteProductCategory = createAsyncThunk('productsCategories/deleteProductCategory', (arg: IDeleteProductCategoryArg, { dispatch }) => {
    const { authToken, productCategoryId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products-categories/${productCategoryId}`;

    dispatch(deleteProductCategoryStart())
    axios.delete(endpoint, {
        headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
        }
    }).then(res => {
        dispatch(deleteProductCategoryPass());
        socket.emit(APP_UPDATE_AVAILABLE, USER);
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }
        let error = '';

        if (data?.err) {
            error = data.err;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(deleteProductCategoryFail(error));
    });
});


export default productsCategoriesSlice.reducer;
